"use client";

import { SHANI_TRANSIT_HEADLINE } from "@/lib/saturn-status";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentTransits } from "@/lib/api/transits";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { getTodayISTDateString } from "@/lib/ist-date";
import { getBrowserQueryClient } from "@/lib/query-client";
import {
  SaturnPositionCard,
  RashiPicker,
  type NextSignLabel,
  type SaturnCardData,
} from "./_sections";
import { bringIntoView } from "./panel-into-view";
import { ShaniRashiSelectionContext, type ShaniRashiSelection } from "./shani-rashi-selection";

export default function ShaniTransitClient({
  crossLinks,
  resultOffer,
  moonCheck = null,
  initialSaturn = null,
  calculationDate,
  nextChange = null,
}: {
  crossLinks: ReactNode;
  resultOffer: ReactNode;
  /**
   * The "find your Moon sign" check (_moon-check/ShaniMoonCheckSection),
   * rendered by the server surface so this module never imports the check,
   * the kundali client or the verdict; it selects a sign here through
   * ShaniRashiSelectionContext.
   */
  moonCheck?: ReactNode;
  /**
   * Today's Saturn from the server's engine read, shown until the client
   * query answers. A display fallback only (never react-query initialData,
   * which would make the query look answered and fire calculator_used early).
   */
  initialSaturn?: SaturnCardData | null;
  /** The server's IST date; the client asks the engine for the same day. */
  calculationDate?: string;
  /** Saturn's next sign from the year datasets, labelled on the server. */
  nextChange?: NextSignLabel | null;
}) {
  const dateStr = useMemo(() => calculationDate ?? getTodayISTDateString(), [calculationDate]);
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);
  const completionTrackedRef = useRef(false);
  const resultPanelRef = useRef<HTMLDivElement>(null);
  const [panelRequest, setPanelRequest] = useState(0);
  const queryRashi = selectedRashi ?? "mesh";

  const { data: transitData, isLoading } = useQuery(
    {
      queryKey: ["transits", "current", queryRashi, dateStr],
      queryFn: () => getCurrentTransits(queryRashi, dateStr),
      staleTime: 30 * 60 * 1000,
      retry: 1,
      // Keep the last reply while the next sign's loads: Saturn's position is
      // the same for every Moon sign, and the offer and card below stay
      // mounted, so a pick (or the Moon-sign check's selection, which lands
      // long after any input) never collapses them. The panel still waits for
      // the picked sign's own reply (selectedReply matches moon_rashi).
      placeholderData: (previous) => previous,
    },
    getBrowserQueryClient()
  );

  const saturnTransit = transitData?.active_transits.find((entry) => entry.planet_id === "saturn");
  const selectedReply =
    selectedRashi && transitData?.moon_rashi === selectedRashi ? transitData : undefined;
  const selectedStatus = selectedReply ? selectedReply.sade_sati : undefined;
  const selectedSaturn = selectedReply?.active_transits.find(
    (entry) => entry.planet_id === "saturn"
  );

  // The check selects a sign (never toggles it off) and asks for the panel,
  // which is always present at its reserved height, to come into view.
  const selectFromCheck = useCallback((rashi: string) => {
    setSelectedRashi(rashi);
    setPanelRequest((count) => count + 1);
  }, []);
  const selection = useMemo<ShaniRashiSelection>(
    () => ({ select: selectFromCheck }),
    [selectFromCheck]
  );

  // Focus follows the scroll: the check's answer (below) would otherwise keep
  // focus, and the next Tab would scroll the reader back down to it. The
  // panel's own effect runs after the answer's focus effect (child first).
  useEffect(() => {
    if (panelRequest === 0) return;
    const panel = resultPanelRef.current;
    bringIntoView(panel);
    panel?.focus({ preventScroll: true });
  }, [panelRequest]);

  useEffect(() => {
    if (!transitData || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackCalculatorUsed({ calculator: "shani-transit", source_path: "/shani-transit" });
  }, [transitData]);

  return (
    <div className="mx-auto min-h-[calc(100vh-130px)] max-w-2xl px-4 py-10 sm:px-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-5xl mb-3">♄</div>
        <h1
          className="text-2xl sm:text-3xl font-bold mb-2"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          {SHANI_TRANSIT_HEADLINE}
        </h1>
        <p className="text-body" style={{ color: "var(--ish-text-muted)" }}>
          Saturn&apos;s current position and which Moon signs are under Sade Sati
        </p>
      </div>

      <SaturnPositionCard
        saturnTransit={saturnTransit ?? initialSaturn}
        calculationDate={dateStr}
        nextChange={nextChange}
      />
      <RashiPicker
        selectedRashi={selectedRashi}
        onSelect={setSelectedRashi}
        sadeSati={selectedStatus}
        saturnTransit={saturnTransit ?? null}
        selectedSaturn={selectedSaturn}
        isLoading={isLoading}
        resultRef={resultPanelRef}
      />

      {moonCheck ? (
        <ShaniRashiSelectionContext.Provider value={selection}>
          {moonCheck}
        </ShaniRashiSelectionContext.Provider>
      ) : null}

      {transitData && resultOffer}

      {crossLinks}
    </div>
  );
}
